import { IReportingPeriod } from "../../model/reporting/ReportingPeriod";
import { IHttpService } from "../HttpService";
import FormVersion, { IFormVersion } from "../../model/form/FormVersion";
import { IFormSection } from "../../model/form/FormSection";
import ReportingPeriodListMapper from "../reporting/mappers/ReportingPeriodListMapper";
import ReportingPeriodFormType, {
  IReportingPeriodFormType
} from "../../model/reporting/ReportingPeriodFormType";
import { formatBooleanValue } from "../..";
import ReferenceValue, {
  IReferenceValue
} from "../../model/form/ReferenceValue";
import PortalForm, { IPortalForm } from "../../model/form/PortalForm";
import BoFormVersion, { IBoFormVersion } from "../../model/form/BoFormVersion";
import FormDetailsMapper from "./mappers/FormDetailsMapper";
import { DateService } from "../DateService";
import Report1Applicant, {
  IReport1Applicant
} from "../../model/report/Report1Applicant";
import FormVersionItem, {
  IFormVersionItem
} from "../../model/report/FormVersionItem";

export interface IFormService {
  getReportingPeriodsByApplicantId(
    applicantId: number
  ): Promise<IReportingPeriod[]>;
  getReportingPeriodFormTypes(
    reportingPeriodApplicantId: number
  ): Promise<IReportingPeriodFormType[]>;
  getFormVersions(formReportingPeriodId: number): Promise<IFormVersion[]>;
  getFormDetails(formVersionId: number): Promise<IFormSection[]>;
  getReferenceValueList(
    applicantId: number,
    attributeCode: string,
    formQualifierId?: number,
    formTypeCode?: string,
    qualifierCode?: string,
    attributeId?: number,
    reportingPeriodId?: number
  ): Promise<IReferenceValue[]>;
  changeFormAttribute(
    formAttributeId: number,
    blob: any,
    valueClob: string,
    valueDate: Date,
    valueNumber: number,
    valueReferenceId: any,
    valueReference: string,
    valueString: string,
    mimeType: string
  ): Promise<any>;
  sendFormVersion(formId: number): Promise<any>;
  deleteDraftVersionForm(formVersionId: number): Promise<any>;
  addRow(formId: number, formSectionId: number): Promise<any>;
  deleteRow(qualifierId: number): Promise<any>;
  undoRowDelete(qualifierId: number): Promise<any>;
  getPreviousReportingPeriodsByApplicantId(
    applicantId: number
  ): Promise<IReportingPeriod[]>;
  getApplicantId(): Promise<number>;
  addNewDraftFormVersion(
    reportingPeriodApplicantId: number,
    reportingPeriodFormTypeId: number,
    formId?: number
  ): Promise<number>;
  getPortalForms(
    reportingPeriodApplicantId: number,
    languageCode: number
  ): Promise<IPortalForm[]>;
  getPortalFormDetails(
    formId: number,
    languageCode: number
  ): Promise<{ formVersion: IFormVersion; formSections: IFormSection[] }>;
  getBoFormTypes(
    reportingPeriodApplicantId: number
  ): Promise<IReportingPeriodFormType[]>;
  getBoFormVersions(formReportingPeriodId: number): Promise<IBoFormVersion[]>;
  validateForm(formId: number): Promise<number[]>;
  checkRightOfAccessToData(
    reportingPeriodApplicantId: number,
    action: string
  ): Promise<boolean>;
  setFormalCheckStatus(formVersionId: number, status: string): Promise<any>;
  getFormReport(
    formVersionId: number,
    languageCode: number
  ): Promise<{
    applicant: IReport1Applicant;
    formVersion: IFormVersionItem;
    formSections: IFormSection[];
  }>;
  hasPdfAccess(): Promise<boolean>;
  getAllowedFileTypes(): Promise<string>;
  uploadSignedReport(formVersionId: number, document: File): Promise<void>;
  isSignedReportRequired(): Promise<boolean>;
}

const FormService = (httpService): IFormService => {
  const _http: IHttpService = httpService;
  const _basePath: string = "/api";
  const _formsPath: string = "/forms";
  const _reportingPeriodsPath: string = "/reporting-periods";
  const _previousReportingPeriodsPath: string = "/previous-reporting-periods";
  const _reportingPeriodFormTypesPath: string = "/reporting-period-form-types";
  const _versionPath: string = "/version";
  const _detailsPath: string = "/details";
  const _valuesPath: string = "/values";
  const _attributePath: string = "/attribute";
  const _qualifiersPath: string = "/qualifiers";
  const _sendVersionPath: string = "/send-version";
  const _formSectionsPath: string = "/form-sections";
  const _addRowPath: string = "/add-row";
  const _deleteRowPath: string = "delete-row";
  const _undoDeleteRowPath: string = "undo-delete-row";
  const _applicantPath: string = "/applicant";
  const _typePath: string = "/type";
  const _applicantsPath: string = "/applicants";
  const _typesPath: string = "/types";
  const _versionsPath: string = "/versions";
  const _validatePath: string = "/validate";
  const _checkRightOfAccessPath: string = "/check-right-of-access";
  const _formalCheckStatus: string = "/formal-check-status";
  const _reportPath: string = "/report";
  const _pdfAccess: string = "/pdf-access";
  const _allowedFileTypesPath: string = "/documents/allowed-file-types";
  const _signedReport: string = "/signed-report";

  const PortalFormListMapper = (json = []) => {
    return json.map((item: any) => {
      return PortalForm(item);
    });
  };

  const ReportingPeriodFormTypeListMapper = (json = []) => {
    return json.map((item: any) => {
      let reportingPeriodFormType = ReportingPeriodFormType(item);
      return reportingPeriodFormType;
    });
  };

  async function buildFormVersionList(data: any): Promise<IFormVersion[]> {
    return Promise.all(
      data.map((item: any) => {
        let formVersion = FormVersion(item);
        formVersion.sendDate = item.sendDate
          ? new Date(item.sendDate)
          : undefined;
        formVersion.signatureDate = item.signatureDate
          ? new Date(item.signatureDate)
          : undefined;
        formVersion.digitallySigned = !!item.digitallySigned;
        return formVersion;
      })
    );
  }

  async function buildBoFormVersionList(data: any): Promise<IBoFormVersion[]> {
    return Promise.all(
      data.map((item: any) => {
        let formVersion = BoFormVersion(item);
        formVersion.forPublication = formatBooleanValue(item.forPublication);
        formVersion.digitallySigned = !!item.digitallySigned;
        formVersion.signatureDate = item.signatureDate
          ? new Date(item.signatureDate)
          : undefined;
        return formVersion;
      })
    );
  }

  const ReferenceValueListMapper = (json = []): IReferenceValue[] => {
    return json.map((item: any) => ReferenceValue(item));
  };

  return {
    async getFormVersions(formReportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _versionPath,
        formReportingPeriodId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return buildFormVersionList(responseJSON);
    },

    async getFormDetails(formVersionId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _detailsPath,
        formVersionId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      const {
        formSections: sections,
        formSectionQualifiers: qualifiers,
        qualifierAttributes: attributes,
        formInputValues: values
      } = await responseJSON;

      return FormDetailsMapper(sections, qualifiers, attributes, values);
    },

    async getReportingPeriodFormTypes(reportingPeriodApplicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _reportingPeriodFormTypesPath,
        reportingPeriodApplicantId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodFormTypeListMapper(responseJSON);
    },

    async getReportingPeriodsByApplicantId(applicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _reportingPeriodsPath
      );
      const response = await _http.get(path, {
        params: { applicantId }
      });
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodListMapper(responseJSON);
    },

    async getReferenceValueList(
      applicantId: number,
      attributeCode: string,
      formQualifierId?: number,
      formTypeCode?: string,
      qualifierCode?: string,
      attributeId?: number,
      reportingPeriodId?: number
    ) {
      const path = _http.buildPath(_basePath, _formsPath, _valuesPath);
      const response = await _http.get(path, {
        params: {
          applicantId,
          attributeCode,
          formQualifierId,
          formTypeCode,
          qualifierCode,
          reportingPeriodAttributeId: attributeId,
          reportingPeriodId
        }
      });
      const responseJSON = await _http.toJSON(response);
      return ReferenceValueListMapper(responseJSON);
    },

    async changeFormAttribute(
      formAttributeId: number,
      blob: any,
      valueClob: string,
      valueDate: Date,
      valueNumber: number,
      valueReferenceId: any,
      valueReference: string,
      valueString: string,
      mimeType: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _attributePath,
        formAttributeId.toString()
      );

      const params = {
        formAttributeId,
        blob,
        valueClob,
        valueDate: valueDate
          ? DateService().parseDateToTimestamp(valueDate)
          : undefined,
        valueNumber,
        valueReferenceId,
        valueReference,
        valueString,
        mimeType
      };

      let response;
      if (blob) {
        response = await _http.uploadFile(path, blob, "blob", [
          { key: "valueString", value: valueString },
          { key: "mimeType", value: mimeType }
        ]);
      } else {
        response = await _http.put(path, {
          form: true,
          params: params
        });
      }
      return _http.toText(response);
    },

    async sendFormVersion(formId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _sendVersionPath,
        formId.toString()
      );

      const response = await _http.put(path);
      return _http.toText(response);
    },

    async deleteDraftVersionForm(formVersionId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _versionPath
      );
      const response = await _http.remove(path);
      return _http.toText(response);
    },

    async addRow(formId: number, formSectionId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formId.toString(),
        _formSectionsPath,
        formSectionId.toString(),
        _addRowPath
      );
      const response = await _http.post(path);
      return _http.toText(response);
    },

    async deleteRow(qualifierId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _qualifiersPath,
        qualifierId.toString(),
        _deleteRowPath
      );
      const response = await _http.remove(path);
      return _http.toText(response);
    },

    async undoRowDelete(qualifierId: number) {
      const path = _http.buildPath(
        _basePath,
        _qualifiersPath,
        qualifierId.toString(),
        _undoDeleteRowPath
      );
      const response = await _http.put(path);
      return _http.toText(response);
    },

    async getPreviousReportingPeriodsByApplicantId(applicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _previousReportingPeriodsPath
      );
      const response = await _http.get(path, {
        params: { applicantId }
      });
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodListMapper(responseJSON);
    },

    async getApplicantId() {
      const path = _http.buildPath(_basePath, _formsPath, _applicantPath);
      const response = await _http.get(path);
      return _http.toJSON(response);
    },

    async addNewDraftFormVersion(
      reportingPeriodApplicantId: number,
      reportingPeriodFormTypeId: number,
      formId?: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        reportingPeriodApplicantId.toString(),
        _typePath,
        reportingPeriodFormTypeId.toString()
      );

      const response = await _http.post(path, {
        form: true,
        params: {
          formId
        }
      });
      return _http.toJSON(response);
    },

    async getPortalForms(
      reportingPeriodApplicantId: number,
      languageCode: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _reportingPeriodsPath,
        _applicantsPath,
        reportingPeriodApplicantId.toString()
      );
      const response = await _http.get(path, {
        params: { languageCode },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      return PortalFormListMapper(responseJSON);
    },

    async getPortalFormDetails(formId: number, languageCode: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _detailsPath,
        formId.toString()
      );
      const response = await _http.get(path, {
        params: { languageCode },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      const {
        formVersion,
        formSections: sections,
        formSectionQualifiers: qualifiers,
        qualifierAttributes: attributes,
        formInputValues: values
      } = await responseJSON;

      return {
        formVersion: FormVersion(formVersion),
        formSections: FormDetailsMapper(
          sections,
          qualifiers,
          attributes,
          values
        )
      };
    },

    async getBoFormTypes(reportingPeriodApplicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _typesPath,
        reportingPeriodApplicantId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ReportingPeriodFormTypeListMapper(responseJSON);
    },

    async getBoFormVersions(formReportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _versionsPath,
        formReportingPeriodId.toString()
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return buildBoFormVersionList(responseJSON);
    },

    async validateForm(formId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formId.toString(),
        _validatePath
      );
      const response = await _http.get(path);
      return _http.toJSON(response);
    },

    async checkRightOfAccessToData(
      reportingPeriodApplicantId: number,
      action: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        reportingPeriodApplicantId.toString(),
        _checkRightOfAccessPath
      );
      const response = await _http.get(path, {
        params: {
          action
        }
      });
      const responseJSON: any = await _http.toJSON(response);
      return !!responseJSON;
    },

    async setFormalCheckStatus(formVersionId: number, status: string) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _formalCheckStatus
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          status
        }
      });
      return _http.toText(response);
    },

    async getFormReport(formVersionId: number, languageCode: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _reportPath,
        formVersionId.toString()
      );
      const response = await _http.get(path, {
        params: {
          languageCode
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      const {
        applicant,
        formVersion,
        formSections,
        formSectionQualifiers,
        qualifierAttributes,
        formInputValues
      } = await responseJSON;

      return {
        applicant: Report1Applicant(applicant),
        formVersion: FormVersionItem(formVersion),
        formSections: FormDetailsMapper(
          formSections,
          formSectionQualifiers,
          qualifierAttributes,
          formInputValues
        )
      };
    },

    async hasPdfAccess() {
      const path = _http.buildPath(_basePath, _formsPath, _pdfAccess);

      const response = await _http.get(path);

      return _http.toJSON(response);
    },

    async getAllowedFileTypes() {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _allowedFileTypesPath
      );

      const response = await _http.get(path);

      return _http.toText(response);
    },

    async uploadSignedReport(formVersionId: number, document: File) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _signedReport
      );

      await _http.postUploadFile(path, document, "document", []);
    },

    async isSignedReportRequired() {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _signedReport,
        "required"
      );
      const response = await _http.get(path);
      return _http.toJSON(response);
    }
  };
};

export default FormService;
